import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { ProgressBar } from "@/components/design/ProgressBar";
import { TextInput } from "@/components/design/TextField";
import { Select } from "@/components/design/Select";
import {
  type BuildJob,
  type CompanyRef,
  type GraphRef,
  GraphExistsError,
  loadBuildableCompanies,
  loadCompanyGraph,
  loadCompanyGraphs,
  loadGraphBuild,
  startGraphBuild,
} from "@/lib/company-graph";

// BUILDING A GRAPH out of companies' own summaries — gate 1 of docs/company-graph-gates.md, as
// something a reader can run. The agreed experiment is 150 companies in and 50 held back, so the
// picking is the real work here: a list of a few hundred rows where you tick most of them, keep a
// running count, and can see at a glance how many you have.
//
// NOT `MultiSelect`. That primitive puts one chip per selection in its trigger and re-filters
// unmemoised on every render, so at 150 selections it is 150 chips and O(N·M) per keystroke. The
// idiom that fits is the one `TeamMemberPicker` uses: rows toggling against a selection set.

const POLL_MS = 2000;
/**
 * When to stop following a build and say so.
 *
 * A POLL THAT ONLY EVER STOPPED AT A FINISHED JOB. Errors were dropped on the floor, so a job that
 * was deleted, or a service that answers 5xx, left the page turning for ever with nothing on it to
 * say the build was no longer being watched. Either bound ends it; the build itself is upstream and
 * carries on regardless, which is why the line says so rather than calling it failed.
 */
const POLL_MAX_FAILURES = 5;
const POLL_MAX_MS = 60 * 60 * 1000;

/** One empty set, so "no base" does not hand `useMemo` a new object on every render. */
const NOBODY: ReadonlySet<string> = new Set();

/** A build id has to survive being a filename, a URL segment and a menu row. */
const idFrom = (name: string) =>
  name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);

/** How often the picker draws a rule — a count a reader can hold without counting rows. */
const TENS = 10;

const terminal = (job: BuildJob | null) => job != null && job.status !== "running";

export default function CompanyGraphBuildPage() {
  const intl = useIntl();
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [name, setName] = useState("");
  const [replace, setReplace] = useState(false);
  const [job, setJob] = useState<BuildJob | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [exists, setExists] = useState(false);
  /** The build is no longer being followed — see `POLL_MAX_FAILURES`. Not the same as failed. */
  const [lost, setLost] = useState(false);
  const [all, setAll] = useState<CompanyRef[] | null>(null);
  const [listFailed, setListFailed] = useState<string | null>(null);
  const [graphs, setGraphs] = useState<GraphRef[]>([]);
  const [base, setBase] = useState("");
  // Who is already IN the base. Offering them would be offering a company the build then refuses,
  // and on the run that matters — fifty held back out of two hundred — it is most of the list.
  //
  // Kept WITH the graph it belongs to and derived, rather than cleared from the effect that starts
  // the next read: writing an empty set there is the cascading-render shape lint objects to, and it
  // would also show the previous graph's companies for a moment after switching.
  const [baseCompanies, setBaseCompanies] = useState<{ for: string; names: Set<string> } | null>(null);

  // THE BUILDER'S OWN LIST, read once. The corpus is a couple of hundred names, so filtering is
  // local and there is nothing to page — and, more importantly, the picker cannot then offer a
  // company the build would report as not in the corpus.
  useEffect(() => {
    let alive = true;
    loadBuildableCompanies().then(
      (it) => alive && setAll(it),
      (error: unknown) => alive && setListFailed(error instanceof Error ? error.message : String(error)),
    );
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    loadCompanyGraphs().then(
      (it) => alive && setGraphs(it),
      () => alive && setGraphs([]),
    );
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!base) return;
    let alive = true;
    loadCompanyGraph(base).then(
      (it) => alive && setBaseCompanies({ for: base, names: new Set(it.companies) }),
      () => {},
    );
    return () => {
      alive = false;
    };
  }, [base]);

  const inBase = base && baseCompanies?.for === base ? baseCompanies.names : NOBODY;

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (all ?? []).filter((it) => !needle || it.name.toLowerCase().includes(needle));
  }, [all, query]);

  // GREYED, NOT HIDDEN. Hiding makes the list shrink for no visible reason; greying shows which
  // part of the corpus the base already covers, which on the run that matters is the whole point —
  // a hundred and fifty in, fifty left.
  const selectable = useMemo(() => shown.filter((it) => !inBase.has(it.name)), [shown, inBase]);
  // Picking a base after choosing companies drops the ones it already holds. The count moving is
  // the signal; sending them would be sending companies the build refuses.
  const sending = useMemo(() => [...chosen].filter((it) => !inBase.has(it)), [chosen, inBase]);

  const id = useMemo(() => idFrom(name), [name]);
  const running = job?.status === "running";

  const toggle = useCallback((company: string) => {
    setChosen((before) => {
      const next = new Set(before);
      if (!next.delete(company)) next.add(company);
      return next;
    });
  }, []);

  // POLLING ONLY WHILE ONE IS IN FLIGHT, cleared on unmount — the shape KnowledgeDocumentsCard
  // uses. A build takes a minute or two; a timer that outlives it is a request every two seconds
  // for as long as the tab is open.
  const jobId = job?.jobId;
  const stop = terminal(job);
  useEffect(() => {
    if (!jobId || stop) return;
    let failures = 0;
    const until = Date.now() + POLL_MAX_MS;
    const timer = window.setInterval(() => {
      if (Date.now() > until) {
        window.clearInterval(timer);
        setLost(true);
        return;
      }
      void loadGraphBuild(jobId).then(
        (it) => {
          failures = 0;
          setJob(it);
        },
        () => {
          failures += 1;
          // ONE REFUSAL IS A HICCUP, five in a row is an answer.
          if (failures >= POLL_MAX_FAILURES) {
            window.clearInterval(timer);
            setLost(true);
          }
        },
      );
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [jobId, stop]);

  const start = async () => {
    setFailed(null);
    setExists(false);
    // Cleared where a build BEGINS rather than in the effect that follows one: resetting it there
    // is a setState on every render the effect is re-run for, which is a cascade waiting to happen.
    setLost(false);
    try {
      setJob(
        await startGraphBuild({
          id,
          name: name.trim(),
          companies: sending,
          replace,
          ...(base ? { base } : {}),
        }),
      );
    } catch (error) {
      // A name already taken is a question for the reader, not a failure: they either meant to
      // replace it or meant a different name, and only they know which.
      if (error instanceof GraphExistsError) setExists(true);
      else setFailed(error instanceof Error ? error.message : String(error));
    }
  };

  const ready = sending.length > 0 && id.length > 0 && !running;
  const counts = job?.counts;

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 p-4 sm:p-6 lg:flex-row">
      {/* THE COMPANIES. The list is the page: picking 150 of 200 one row at a time is the task. */}
      <section className="box bg-surface flex min-h-0 flex-1 flex-col">
        <div className="border-subtle flex flex-wrap items-center gap-3 border-b px-4 py-3">
          <h2 className="text-smycles-body uppercase tracking-[0.18em] text-ink-muted">
            <FormattedMessage id="graphBuild.companies" defaultMessage="Companies" />
          </h2>
          {/* Both ways: a reader who builds one wants to look at it, and a reader looking at one
              wants to build the next. The link the other way is on the graph's own header. */}
          <Link to="/admin/company-graph" className="text-smycles-caption underline">
            <FormattedMessage id="graphBuild.toGraph" defaultMessage="Back to the graph" />
          </Link>
          <TextInput
            id="graph-build-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={intl.formatMessage({ id: "graphBuild.search", defaultMessage: "Filter by name…" })}
            aria-label={intl.formatMessage({ id: "graphBuild.search", defaultMessage: "Filter by name…" })}
            className="max-w-xs flex-1"
          />
          <span className="text-smycles-caption tabular-nums text-ink-muted">
            <FormattedMessage
              id="graphBuild.chosenCount"
              defaultMessage="{count} selected"
              values={{ count: sending.length }}
            />
          </span>
          <Button
            variant="ghost"
            onClick={() => setChosen((before) => new Set([...before, ...selectable.map((it) => it.name)]))}
            disabled={selectable.length === 0 || running}
          >
            <FormattedMessage id="graphBuild.selectShown" defaultMessage="Select all shown" />
          </Button>
          <Button variant="ghost" onClick={() => setChosen(new Set())} disabled={chosen.size === 0 || running}>
            <FormattedMessage id="graphBuild.clear" defaultMessage="Clear" />
          </Button>
        </div>
        <ul className="min-h-0 flex-1 overflow-y-auto p-2">
          {shown.map((company, index) => {
            const already = inBase.has(company.name);
            return (
              <Fragment key={company.id}>
                {/* A RULE EVERY TEN, CARRYING ITS OWN NUMBER. Picking a hundred out of two hundred
                    and thirty-six by eye means counting, and a bare rule only moves the counting
                    from rows to rules. `aria-hidden` because it is a ruler, not an entry. */}
                {index > 0 && index % TENS === 0 && (
                  <li aria-hidden className="flex items-center gap-2 px-3 py-1">
                    <span className="bg-ink/10 h-px flex-1" />
                    <span className="text-smycles-caption text-ink-muted tabular-nums opacity-60">{index}</span>
                  </li>
                )}
              <li className="flex items-center gap-1">
                <label
                  className={`flex min-w-0 flex-1 items-center gap-3 rounded-md px-3 py-2 transition ${
                    already ? "cursor-not-allowed opacity-40" : "cursor-pointer hover:bg-ink/[0.04]"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={!already && chosen.has(company.name)}
                    onChange={() => toggle(company.name)}
                    disabled={running || already}
                  />
                  <span className="min-w-0 flex-1 truncate text-smycles-body text-ink">{company.name}</span>
                  {already && (
                    <span className="shrink-0 text-smycles-caption text-ink-muted">
                      <FormattedMessage id="graphBuild.alreadyIn" defaultMessage="already in" />
                    </span>
                  )}
                </label>
                {/* OUTSIDE THE LABEL, or it would toggle the checkbox on its way to the company:
                    a link inside a label is a click on the label too, and a reader checking who a
                    company is would silently add or drop it from the build.
                    A NEW TAB, because the selection is state on this page and nothing else. Coming
                    back from the company page would leave a reader to pick a hundred of two
                    hundred and thirty-six all over again. */}
                <Link
                  to={`/c/${company.id}`}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={intl.formatMessage(
                    { id: "graphBuild.openCompany", defaultMessage: "Open {name}" },
                    { name: company.name },
                  )}
                  className="shrink-0 rounded-md px-2 py-2 text-smycles-caption text-ink-muted underline underline-offset-2 transition hover:text-ink"
                >
                  <FormattedMessage id="graphBuild.open" defaultMessage="open" />
                </Link>
              </li>
              </Fragment>
            );
          })}
          {/* A LIST THAT FAILED TO LOAD IS NOT AN EMPTY CORPUS. Rendering both the same way is how
              this page first read as "no companies" when something else entirely was wrong. */}
          {listFailed && (
            <li className="px-3 py-2 text-smycles-body text-ink-attention">
              <FormattedMessage
                id="graphBuild.companiesFailed"
                defaultMessage="The companies could not be read: {error}"
                values={{ error: listFailed }}
              />
            </li>
          )}
          {all != null && shown.length === 0 && !listFailed && (
            <li className="px-3 py-2 text-smycles-body text-ink-muted">
              <FormattedMessage id="graphBuild.noCompanies" defaultMessage="No companies match that." />
            </li>
          )}
          {all == null && !listFailed && (
            <li className="px-3 py-2 text-smycles-body text-ink-muted">
              <FormattedMessage id="graphBuild.loadingCompanies" defaultMessage="Reading the companies…" />
            </li>
          )}
        </ul>
      </section>

      {/* THE BUILD. Name, start, then what each stage took in and let through. */}
      <section className="box bg-surface flex w-full shrink-0 flex-col gap-3 p-4 lg:w-[340px]">
        <h2 className="text-smycles-body uppercase tracking-[0.18em] text-ink-muted">
          <FormattedMessage id="graphBuild.title" defaultMessage="Build a graph" />
        </h2>

        {graphs.length > 0 && (
          <label className="block">
            <span className="smycles-field-label">
              <FormattedMessage id="graphBuild.base" defaultMessage="Start from" />
            </span>
            <Select
              id="graph-build-base"
              className="mt-1 w-full"
              value={base}
              onChange={setBase}
              disabled={running}
              placeholder={intl.formatMessage({ id: "graphBuild.fromNothing", defaultMessage: "Nothing" })}
              options={graphs.map((it) => ({ value: it.id, label: it.name }))}
            />
          </label>
        )}

        <label className="block">
          <span className="smycles-field-label">
            <FormattedMessage id="graphBuild.name" defaultMessage="Name" />
          </span>
          <TextInput
            id="graph-build-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={running}
            placeholder={intl.formatMessage({ id: "graphBuild.namePlaceholder", defaultMessage: "Extracted, 150" })}
            className="mt-1 w-full"
          />
        </label>
        {id && (
          <p className="text-smycles-caption text-ink-muted">
            <FormattedMessage id="graphBuild.id" defaultMessage="Id: {id}" values={{ id }} />
          </p>
        )}

        {exists && (
          <label className="flex items-center gap-2 text-smycles-caption text-ink">
            <input type="checkbox" checked={replace} onChange={(event) => setReplace(event.target.checked)} />
            <FormattedMessage
              id="graphBuild.replace"
              defaultMessage="A graph called that exists. Replace it, and anyone searching it now gets the new one."
            />
          </label>
        )}

        <Button onClick={() => void start()} disabled={!ready}>
          <FormattedMessage id="graphBuild.start" defaultMessage="Build" />
        </Button>

        {failed && <p className="text-smycles-caption text-ink-attention">{failed}</p>}
        {lost && (
          <p role="status" className="text-smycles-caption text-ink-attention">
            <FormattedMessage
              id="graphBuild.lost"
              defaultMessage="This build can no longer be followed from here. It may still be running – reload the page to pick it up again."
            />
          </p>
        )}

        {job && (
          <div className="flex flex-col gap-2">
            <ProgressBar
              label={job.stage}
              done={job.status === "running" ? job.done : job.total}
              total={job.total}
              valueAlreadyVisible
            />
            <p className="text-smycles-caption text-ink-muted">
              {job.stage}
              {job.total != null ? ` — ${job.done}/${job.total}` : ""}
            </p>
            {job.status === "failed" && (
              <p className="text-smycles-caption text-ink-attention">
                <FormattedMessage
                  id="graphBuild.failedAt"
                  defaultMessage="Failed while {stage}: {error}"
                  values={{ stage: job.stage, error: job.error ?? "" }}
                />
              </p>
            )}
            {job.warning && <p className="text-smycles-caption text-ink-attention">{job.warning}</p>}
            {/* THE WAY OUT OF THIS PAGE, and the one a reader wants: the graph they just built is
                not the active one, so the link at the top opens somebody else's graph and leaves
                them to find theirs among twelve names in a picker. Named, because the `?id=` is
                what makes this different from that link. */}
            {job.status === "done" && (
              <Link to={`/admin/company-graph?id=${encodeURIComponent(job.graphId)}`} className="underline">
                <FormattedMessage
                  id="graphBuild.openBuilt"
                  defaultMessage="Open ‘{graph}’"
                  values={{ graph: job.graphId }}
                />
              </Link>
            )}
            {counts && (
              // EVERY STAGE, not a total. A graph that disappoints has to be attributable to a
              // stage — the model invented edges, the clustering fragmented the vocabulary, the
              // summaries said nothing — and one number tells a reader none of that.
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-smycles-caption">
                {(
                  [
                    ["graphBuild.count.fetched", "Summaries read", `${counts.fetched}/${counts.asked}`],
                    // THE OTHER TWO SOURCES, shown only where there are any: an instance that
                    // builds from summaries alone must not read a permanent "Akte items 0" as a
                    // failure of something it never asked for.
                    ...(counts.texts
                      ? ([["graphBuild.count.texts", "Texts asked about", `${counts.texts}`]] as const)
                      : []),
                    ...(counts.recordItems
                      ? ([["graphBuild.count.recordItems", "From the organisational record", `${counts.recordItems}`]] as const)
                      : []),
                    ...(counts.labelsRead
                      ? ([
                          ["graphBuild.count.labelsRead", "Expertise labels read", `${counts.labelsRead}`],
                          ["graphBuild.count.labelEdges", "Relations from labels", `${counts.labelEdges ?? 0}`],
                        ] as const)
                      : []),
                    ["graphBuild.count.proposed", "Relations proposed", `${counts.proposed}`],
                    ["graphBuild.count.unfaithful", "Discarded, no evidence", `${counts.unfaithful}`],
                    ["graphBuild.count.spellings", "Wordings the model used", `${counts.spellings}`],
                    ["graphBuild.count.nodes", "Things in the graph", `${counts.nodes}`],
                    ["graphBuild.count.edges", "Relations added", `${counts.edges}`],
                    ["graphBuild.count.unreadable", "Unusable replies", `${counts.unreadable}`],
                    ["graphBuild.count.dangling", "Customers outside the set", `${counts.danglingCustomers}`],
                    ...(job.base
                      ? ([
                          // THE THREE OUTCOMES THAT DECIDE WHETHER A GRAPH CAN GROW, apart. A word
                          // the model reused is the graph growing; one we folded on is drift we
                          // caught; one it coined may be either.
                          ["graphBuild.count.reused", "Taken from the graph", `${counts.reused}`],
                          ["graphBuild.count.rescued", "Merged in afterwards", `${counts.rescued}`],
                          ["graphBuild.count.coined", "Newly named", `${counts.coined}`],
                        ] as const)
                      : []),
                  ] as const
                ).map(([key, fallback, value]) => (
                  <div key={key} className="contents">
                    <dt className="text-ink-muted">
                      <FormattedMessage id={key} defaultMessage={fallback} />
                    </dt>
                    <dd className="tabular-nums text-ink">{value}</dd>
                  </div>
                ))}
              </dl>
            )}
            {counts?.recordUnreadable != null && counts.recordUnreadable.length > 0 && (
              <details className="text-smycles-caption text-ink-attention">
                <summary className="cursor-pointer">
                  <FormattedMessage
                    id="graphBuild.recordUnreadable"
                    defaultMessage="{count, plural, one {# filed document could not be read} other {# filed documents could not be read}}"
                    values={{ count: counts.recordUnreadable.length }}
                  />
                </summary>
                <ul className="mt-1 list-disc pl-4">
                  {counts.recordUnreadable.map((it) => (
                    <li key={it}>{it}</li>
                  ))}
                </ul>
              </details>
            )}
            {counts != null && counts.suspicious.length > 0 && (
              <div className="text-smycles-caption text-ink-attention">
                <FormattedMessage
                  id="graphBuild.suspicious"
                  defaultMessage="New things that look like ones the graph already had:"
                />
                <ul className="mt-1 list-disc pl-4">
                  {counts.suspicious.map((it) => (
                    <li key={it}>{it}</li>
                  ))}
                </ul>
              </div>
            )}
            {counts != null && (counts.namedACompany?.length ?? 0) > 0 && (
              <div className="text-smycles-caption text-ink-attention">
                <FormattedMessage
                  id="graphBuild.namedACompany"
                  defaultMessage="Named as things, but they are companies here, so they were dropped:"
                />
                <ul className="mt-1 list-disc pl-4">
                  {counts.namedACompany!.map((it) => (
                    <li key={it}>{it}</li>
                  ))}
                </ul>
              </div>
            )}
            {counts != null && (counts.silent?.length ?? 0) > 0 && (
              <div className="text-smycles-caption text-ink-attention">
                <FormattedMessage
                  id="graphBuild.silent"
                  defaultMessage="Asked, but contributed nothing:"
                />
                <ul className="mt-1 list-disc pl-4">
                  {counts.silent!.map((it) => (
                    <li key={it}>{it}</li>
                  ))}
                </ul>
              </div>
            )}
            {counts != null && counts.notFound.length > 0 && (
              <p className="text-smycles-caption text-ink-attention">
                <FormattedMessage
                  id="graphBuild.notFound"
                  defaultMessage="Not in the corpus: {names}"
                  values={{ names: counts.notFound.join(", ") }}
                />
              </p>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
